import { Injectable, OnDestroy, inject, signal } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NotificationService } from '../shared/notifications/notification.service';
import { MfaReminderDialogComponent, MfaReminderResult } from './mfa-reminder-dialog/mfa-reminder-dialog.component';
import { AuthService } from '../shared/authentication/auth.service';
import { AppConfig } from '../app.config';
import { LogHelper } from '../shared/logging/log-helper';

/**
 * Marker query param Balsa adds to the returnUrl it hands Alder's MFA self-service page. The Alder "Back"
 * link returns the user to that URL verbatim (full page load, same tab), so the marker tells the freshly
 * bootstrapped SPA that it just came back from enrollment and must pull a NEW token before reading the
 * `mfa_enabled` claim — the token rehydrated from storage predates enrollment and would still read stale.
 */
export const AUTH_REFRESH_PARAM = 'authRefresh';

@Injectable({ providedIn: 'root' })
export class MfaService implements OnDestroy {
  private readonly MFA_MANAGEMENT_PAGE_URL = AppConfig.hosts.alderApp + '/account/manage';

  private readonly authService = inject(AuthService);
  private readonly dialog = inject(MatDialog);
  private readonly notificationService = inject(NotificationService);
  private readonly log = new LogHelper('Mfa');

  private readonly incomplete = signal(false);

  /** The in-flight {@link syncStatus} run, if any — see the coalescing note there. */
  private syncing: Promise<void> | null = null;

  /** The open reminder dialog, if any — used to keep a second one from stacking on top. */
  private dialogRef: MatDialogRef<MfaReminderDialogComponent> | null = null;

  /**
   * Whether Balsa owns this user's MFA enrollment at all. SSO logins authenticate against an external
   * IdP that enforces its own MFA policy, and Alder's self-service page cannot manage factors for an
   * account it does not own — so for SSO users Balsa neither prompts for enrollment nor offers the
   * setup entry points. Every path into MFA setup (auto-prompt, notification, account menu) funnels
   * through this check.
   */
  canManageMfa(): boolean {
    return !this.authService.isSSO();
  }

  private readonly onPageShow = (event: PageTransitionEvent): void => {
    if (event.persisted && this.hasAuthRefreshMarker()) {
      void this.syncStatus();
    }
  };

  private snoozeKey(): string {
    return `mfa-reminder-snoozed-${this.authService.user.snapshot?.identity?.id ?? 'anon'}`;
  }

  constructor() {
    // A return from Alder can be served from the browser's back/forward cache, which restores the page
    // without re-running bootstrap (so syncStatus never fires). Re-run it on a persisted pageshow when
    // the marker is present. Scoped to the marker so it never fires on ordinary tab switches/restores.
    globalThis.addEventListener('pageshow', this.onPageShow);

    this.notificationService.registerRule({
      id: 'mfa-incomplete',
      condition: () => this.incomplete(),
      notification: {
        type: 'account',
        section: 'security',
        title: 'Finish setting up MFA',
        message: 'Multi Factor Authentication will be required starting August 3, 2026.',
        createdAt: new Date(),
        action: () => this.openDialog(),
      },
    });
  }

  /**
   * Call after user loads — recomputes enrollment state and prompts if needed.
   *
   * Overlapping calls share one run. LayoutComponent drives this from a `user.data` subscription and
   * that stream emits twice on a cold load (the persisted user, then the revalidated one), so two runs
   * routinely overlap. On a return from Alder the first run suspends on the forced token refresh, which
   * left the second free to evaluate the *stale* pre-enrollment claim — prompting a user who had just
   * enrolled, and stacking a second dialog on one already open. Coalescing is per-run, not permanent:
   * a later call (e.g. a bfcache pageshow) still re-evaluates.
   */
  async syncStatus(): Promise<void> {
    this.syncing ??= this.runSyncStatus().finally(() => {
      this.syncing = null;
    });

    return this.syncing;
  }

  private async runSyncStatus(): Promise<void> {
    await this.refreshIfReturningFromMfa();

    const incomplete =
      !this.authService.isSSO() &&
      !this.authService.isSuperAdmin() &&
      !this.authService.isImpersonating() &&
      this.authService.hasMfa() &&
      !this.authService.isMfaEnrolled();

    this.incomplete.set(incomplete);

    if (incomplete && !sessionStorage.getItem(this.snoozeKey())) {
      this.openDialog();
    }
  }

  manageMfa(): void {
    if (!this.canManageMfa()) {
      this.log.warn('manageMfa ignored — MFA for SSO logins is managed by the identity provider');
      return;
    }

    // The user is proactively going to Alder to manage MFA, so drop any "remind me later" deferral.
    // Otherwise the snooze key survives the round-trip and would suppress the dialog on return, even
    // though we want the post-return state evaluated fresh (and re-prompted if still not enrolled).
    sessionStorage.removeItem(this.snoozeKey());

    const returnUrl = encodeURIComponent(this.buildReturnUrl());
    globalThis.window.open(`${this.MFA_MANAGEMENT_PAGE_URL}?returnUrl=${returnUrl}`, '_self');
  }

  /**
   * Builds the returnUrl Alder sends the user back to, tagged with the {@link AUTH_REFRESH_PARAM} marker.
   * The marker goes in the query string (before the hash route) via the URL API so it survives hash-based
   * routing and is readable from `location.search` on return.
   */
  private buildReturnUrl(): string {
    const url = new URL(globalThis.location.href);
    url.searchParams.set(AUTH_REFRESH_PARAM, '1');
    return url.toString();
  }

  /**
   * If we just returned from Alder's MFA self-service page, force a fresh token so the `mfa_enabled` claim
   * reflects the just-completed enrollment, then strip the marker so a manual refresh doesn't renew again.
   * The marker is cleared up front so a failed renew is still one-shot (we proceed with the existing token).
   */
  private async refreshIfReturningFromMfa(): Promise<void> {
    if (!this.hasAuthRefreshMarker()) {
      return;
    }

    this.clearAuthRefreshMarker();

    try {
      await this.authService.forceRefreshAccessToken();
    } catch (err) {
      this.log.warn('forced token refresh after MFA return failed', err);
    }
  }

  private hasAuthRefreshMarker(): boolean {
    return new URLSearchParams(globalThis.location.search).has(AUTH_REFRESH_PARAM);
  }

  private clearAuthRefreshMarker(): void {
    const url = new URL(globalThis.location.href);
    url.searchParams.delete(AUTH_REFRESH_PARAM);
    globalThis.history.replaceState({}, globalThis.document.title, url.toString());
  }

  ngOnDestroy(): void {
    globalThis.removeEventListener('pageshow', this.onPageShow);
  }

  openDialog(): void {
    if (!this.canManageMfa()) {
      this.log.warn('openDialog ignored — MFA for SSO logins is managed by the identity provider');
      return;
    }

    // The reminder is reachable from several places at once (a sync run, the notification action, the
    // account menu) and MatDialog stacks instances without complaint. The snooze key cannot stand in
    // for this guard — it is only written once the dialog closes, so it is still absent while one is up.
    if (this.dialogRef) {
      return;
    }

    this.dialogRef = this.dialog.open(MfaReminderDialogComponent, {
      width: '520px',
      autoFocus: false,
      disableClose: false,
    });

    this.dialogRef.afterClosed().subscribe((result: MfaReminderResult) => {
      this.dialogRef = null;

      if (result === 'setup') {
        this.manageMfa();
      } else {
        sessionStorage.setItem(this.snoozeKey(), '1');
      }
    });
  }
}
